import { faHeart } from '@fortawesome/free-regular-svg-icons/faHeart';
import { faHeart as faHeartFilled } from '@fortawesome/free-solid-svg-icons/faHeart';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { StyledFontAwesomeIconMui5 } from 'components/atoms/FontAwesomeIcon';
import { StyledButtonMui5 } from 'components/atoms/StyledButton';
import { useForwardToDialog } from 'hooks/useForwardToDialog';
import { useWatchAuction } from 'hooks/useWatchAuction';
import { useRouter } from 'next/router';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'recoil/atoms';
import { Auction } from 'shared/types/models';
import { lightThemeMui5 } from 'theme';

type ClientProps = Props & {
  ssrLiked: boolean;
};

type Props = {
  auction: Auction;
  isButton?: boolean;
};

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  '&.MuiButtonBase-root': {
    padding: theme.spacing(1.25),
    marginRight: theme.spacing(-1),
  },
}));

const LikeClient = ({ ssrLiked, ...props }: ClientProps): JSX.Element => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const ssrOnlyLike = props.isButton ? (
    <StyledButtonMui5
      onClick={() => {}}
      variant='outlined'
      color='primary'
      size='small'
      sx={{ height: 32 }}
      startIcon={
        <StyledFontAwesomeIconMui5 icon={ssrLiked ? faHeartFilled : faHeart} />
      }
    >
      {ssrLiked ? 'Unwatch' : 'Watch'}
    </StyledButtonMui5>
  ) : (
    <StyledIconButton onClick={() => {}} aria-label='settings' size='small'>
      <StyledFontAwesomeIconMui5
        icon={ssrLiked ? faHeartFilled : faHeart}
        color={lightThemeMui5.palette.secondary.main}
        size={'lg'}
      />
    </StyledIconButton>
  );

  return isClient ? <Like {...props} /> : ssrOnlyLike;
};

const Like = ({ auction, isButton }: Props): JSX.Element => {
  const user = useRecoilValue(userAtom);
  const { isWatchingAuction, toggleWatchAuction } = useWatchAuction();

  const isLiked = isWatchingAuction(auction.id);

  const router = useRouter();

  const [setSignInDialogOpened] = useForwardToDialog({
    title: 'Please sign in',
    content: <>You need to sign in or create an account</>,
    redirectTo: `/login?returnTo=${encodeURIComponent(router.asPath)}`,
    submitText: 'Sign In',
  });

  const handleClick = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!user) {
      return setSignInDialogOpened(true);
    }

    toggleWatchAuction(auction.id);
  };

  if (isButton) {
    return (
      <StyledButtonMui5
        onClick={handleClick}
        variant='outlined'
        color='primary'
        size='small'
        sx={{ height: 32 }}
        startIcon={
          <StyledFontAwesomeIconMui5 icon={isLiked ? faHeartFilled : faHeart} />
        }
        className={isLiked ? 'js-unwatch-button' : 'js-watch-button'}
      >
        {isLiked ? 'Unwatch' : 'Watch'}
      </StyledButtonMui5>
    );
  }

  return (
    <StyledIconButton
      onClick={handleClick}
      aria-label='settings'
      size='small'
      className={isLiked ? 'js-unwatch-button' : 'js-watch-button'}
    >
      <StyledFontAwesomeIconMui5
        icon={isLiked ? faHeartFilled : faHeart}
        color={lightThemeMui5.palette.secondary.main}
        size={'lg'}
      />
    </StyledIconButton>
  );
};

export default LikeClient;
